import type { ChatMessage } from '@/types';
import type { ReactNode } from 'react';

// ── Attachment display ────────────────────────────────────────────────
function AttachmentDisplay({ message }: { message: ChatMessage }) {
  if (!message.attachments?.length) return null;
  return (
    <div className="flex flex-col gap-1.5 mb-2">
      {message.attachments.map((att, i) => {
        if (att.type === 'image') {
          return att.data ? (
            <img
              key={i}
              src={`data:${att.mimeType};base64,${att.data}`}
              alt={att.name ?? 'Attached image'}
              width={320}
              height={192}
              loading="lazy"
              className="max-w-full rounded-lg max-h-48 object-cover"
            />
          ) : (
            <span key={i} className="flex items-center gap-1 text-xs opacity-75">
              🖼 {att.name ?? 'image'}
            </span>
          );
        }
        return (
          <span key={i} className="flex items-center gap-1 text-xs opacity-75">
            {att.type === 'audio' ? '🎵' : '📎'} {att.name ?? att.mimeType}
          </span>
        );
      })}
    </div>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  /** Optional per-response feedback handler. Provided only for assistant replies that can be rated. */
  onFeedback?: (value: 'up' | 'down') => void;
  /** Localised labels for the thumbs controls. */
  feedbackLabels?: { up: string; down: string; thanks: string };
}

// ── Inline markdown parser ────────────────────────────────────────────
// Handles **bold**, [text](url), and plain text spans within a single line.
function parseInline(text: string): ReactNode[] {
  const pattern = /(\*\*[^*\n]+\*\*|\[[^\]\n]+\]\([^)\n]+\))/g;
  const nodes: ReactNode[] = [];
  let last = 0;
  let idx  = 0;
  let m: RegExpExecArray | null;

  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const raw = m[0];
    if (raw.startsWith('**')) {
      nodes.push(<strong key={idx++}>{raw.slice(2, -2)}</strong>);
    } else {
      const lm = raw.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm) {
        // Only safe URL schemes — model/history output can contain
        // javascript:/data: or protocol-relative links (XSS/phishing vector).
        const href = lm[2].replace(/[\u0000-\u0020]/g, '');
        if (/^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(href)) {
          nodes.push(
            <a
              key={idx++}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 underline underline-offset-2 hover:text-primary-600 transition-colors"
            >
              {lm[1]}
            </a>,
          );
        } else {
          nodes.push(lm[1]); // unsafe scheme → keep the text, drop the link
        }
      } else {
        nodes.push(raw);
      }
    }
    last = m.index + raw.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// ── Block markdown renderer ───────────────────────────────────────────
// Splits on double-newlines for paragraphs; single-leading "- " for lists.
function renderMarkdown(text: string): ReactNode {
  const blocks = text.split(/\n{2,}/);

  return (
    <div className="space-y-1.5">
      {blocks.map((block, bi) => {
        const lines   = block.split('\n');
        const bullets = lines.filter(l => /^[-•]\s/.test(l));
        const isList  = bullets.length > 0 && bullets.length === lines.filter(l => l.trim()).length;

        if (isList) {
          return (
            <ul key={bi} className="list-disc list-inside space-y-0.5">
              {bullets.map((l, li) => (
                <li key={li}>{parseInline(l.slice(2))}</li>
              ))}
            </ul>
          );
        }

        return (
          <p key={bi} className="leading-relaxed">
            {lines.map((line, li) => (
              <span key={li}>
                {li > 0 && <br />}
                {parseInline(line)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

export function MessageBubble({ message, onFeedback, feedbackLabels }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  // Show thumbs only on assistant replies that have actual content and a handler.
  const canRate = !isUser && !!onFeedback && !!message.content.trim();

  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-primary-500/10 border border-primary-500/20 flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-xs font-bold text-primary-500" aria-hidden="true">Z</span>
        </div>
      )}

      <div className={`flex flex-col gap-1 max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Bubble */}
        <div
          className={[
            'w-fit max-w-full rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
            isUser
              ? 'bg-primary-500 text-neutral-900 rounded-tr-sm'
              : 'bg-(--surface) border border-(--border) text-(--text) rounded-tl-sm',
          ].join(' ')}
        >
          <AttachmentDisplay message={message} />
          {isUser
            ? message.content && <p className="whitespace-pre-wrap wrap-break-word">{message.content}</p>
            : <div className="wrap-break-word">{renderMarkdown(message.content)}</div>
          }
          <time
            dateTime={message.createdAt}
            className={`block mt-1 text-xs ${isUser ? 'text-white/60 text-end' : 'text-(--muted)'}`}
          >
            {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </time>
        </div>

        {/* Per-response thumbs (optional) */}
        {canRate && (
          <div className="flex items-center gap-1 ps-1">
            <button
              type="button"
              onClick={() => onFeedback!('up')}
              aria-pressed={message.feedback === 'up'}
              aria-label={feedbackLabels?.up ?? 'Good response'}
              title={feedbackLabels?.up ?? 'Good response'}
              className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-150 cursor-pointer hover:bg-(--surface) ${
                message.feedback === 'up' ? 'text-primary-500' : 'text-(--muted) hover:text-(--text)'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill={message.feedback === 'up' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => onFeedback!('down')}
              aria-pressed={message.feedback === 'down'}
              aria-label={feedbackLabels?.down ?? 'Bad response'}
              title={feedbackLabels?.down ?? 'Bad response'}
              className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-150 cursor-pointer hover:bg-(--surface) ${
                message.feedback === 'down' ? 'text-red-500' : 'text-(--muted) hover:text-(--text)'
              }`}
            >
              <svg className="w-3.5 h-3.5 rotate-180" fill={message.feedback === 'down' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
              </svg>
            </button>
            {message.feedback && (
              <span className="text-xs text-(--muted) ps-0.5 animate-fade-in">{feedbackLabels?.thanks ?? 'Thanks!'}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageBubble;
